import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { Users, ArrowRight, Clock, ChefHat, CheckCircle2, Eye, RefreshCw, QrCode, X, Printer as PrinterIcon } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

const Terminali = () => {
    const [waiters, setWaiters] = useState([]);
    const [summary, setSummary] = useState(null);
    const [qrTokens, setQrTokens] = useState({}); // user_id -> token
    const [showQr, setShowQr] = useState(null); // user_id of expanded QR
    const [printAll, setPrintAll] = useState(false);
    const navigate = useNavigate();
    const { lastEvent } = useWS();

    const load = async () => {
        try {
            const [w, s] = await Promise.all([
                api.get("/terminals/waiters"),
                api.get("/terminals/summary"),
            ]);
            setWaiters(w.data);
            setSummary(s.data);
            // Pre-fetch QR token per ogni cameriere attivo (idempotente: rinnova solo se mancante)
            const tokens = {};
            await Promise.all((w.data || []).filter((u) => u.active).map(async (u) => {
                try {
                    const { data } = await api.get(`/users/${u.user_id}/qr-login`);
                    tokens[u.user_id] = data.token;
                } catch (_e) { /* ignore */ }
            }));
            setQrTokens(tokens);
        } catch (_e) { /* ignore */ }
    };
    useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
    useEffect(() => {
        if (!lastEvent) return;
        if (["table_updated", "new_kitchen_order", "item_ready", "payment_completed"].includes(lastEvent.type)) load();
    }, [lastEvent]);

    const rotateToken = async (uid) => {
        if (!window.confirm("Rigenerare il QR? Il vecchio QR non sarà più valido.")) return;
        try {
            const { data } = await api.post(`/users/${uid}/qr-login`);
            setQrTokens((p) => ({ ...p, [uid]: data.token }));
            toast.success("QR rigenerato");
        } catch (e) { toast.error(formatError(e)); }
    };

    const buildQrUrl = (token) => `${window.location.origin}/qr-login?token=${encodeURIComponent(token)}`;

    return (
        <div className="space-y-6" data-testid="terminali-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Multi-Terminale</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <Users className="w-9 h-9 text-brand" /> Terminali Camerieri
                    </h1>
                    <p className="text-elegant mt-2 max-w-2xl">
                        Ogni cameriere ha il proprio QR personale.
                        <span className="text-brand"> Scansiona dal telefono</span> per accedere istantaneamente al terminale del cameriere.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setPrintAll(true)} data-testid="print-all-qr-button"
                        className="h-12 px-5 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand-soft">
                        <PrinterIcon className="w-4 h-4" /> Stampa tutti i QR
                    </button>
                    <button onClick={() => navigate("/cucina")} data-testid="open-kitchen" className="h-12 px-5 border border-[#1f2c44] rounded-md flex items-center gap-2 hover:border-brand">
                        <ChefHat className="w-4 h-4" /> Cucina
                    </button>
                    <button onClick={() => navigate("/cassa")} data-testid="open-cassa" className="h-12 px-5 border border-[#1f2c44] rounded-md flex items-center gap-2 hover:border-brand">
                        <CheckCircle2 className="w-4 h-4" /> Cassa
                    </button>
                    <button onClick={load} className="h-12 w-12 border border-[#1f2c44] rounded-md flex items-center justify-center hover:border-brand" title="Ricarica">
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Camerieri attivi</div>
                        <div className="font-display text-3xl font-bold text-brand">{summary.waiters}</div>
                    </div>
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Cucina</div>
                        <div className="font-display text-3xl font-bold">{summary.kitchen}</div>
                    </div>
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Cassieri</div>
                        <div className="font-display text-3xl font-bold">{summary.cashiers}</div>
                    </div>
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Ordini aperti</div>
                        <div className="font-display text-3xl font-bold text-brand">{summary.open_orders}</div>
                    </div>
                </div>
            )}

            {waiters.length === 0 && (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-12 text-center">
                    <Users className="w-12 h-12 text-brand mx-auto mb-4 opacity-60" />
                    <div className="font-display text-xl font-bold text-warm mb-2">Nessun cameriere creato</div>
                    <div className="text-elegant text-sm mb-4">
                        Crea il primo account cameriere dalla pagina Utenti.
                    </div>
                    <button onClick={() => navigate("/utenti")} className="h-12 px-5 bg-brand text-deep font-bold rounded-md">
                        Vai a Utenti
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {waiters.map((w) => {
                    const token = qrTokens[w.user_id];
                    const qrUrl = token ? buildQrUrl(token) : null;
                    const expanded = showQr === w.user_id;
                    return (
                        <div key={w.user_id} className="bg-surface border border-[#1f2c44] rounded-lg p-5 flex flex-col" data-testid={`waiter-card-${w.user_id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-surface-elevated border border-brand/40 flex items-center justify-center text-brand font-display font-bold text-lg">
                                        {w.name?.[0]?.toUpperCase() || "?"}
                                    </div>
                                    <div>
                                        <div className="font-display font-bold text-warm">{w.name}</div>
                                        <div className="text-[11px] text-elegant">{w.email}</div>
                                    </div>
                                </div>
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${w.active ? "bg-brand-soft text-brand" : "bg-red-900/30 text-red-300"}`}>
                                    {w.active ? "Attivo" : "Disattivo"}
                                </span>
                            </div>

                            {/* QR di accesso */}
                            {w.active && qrUrl && (
                                <div className={`mt-1 mb-3 rounded-lg overflow-hidden border border-[#1f2c44] ${expanded ? "bg-white p-3" : "bg-white p-2"}`}
                                    data-testid={`waiter-qr-${w.user_id}`}>
                                    <div className="flex justify-center">
                                        <QRCodeSVG value={qrUrl} size={expanded ? 220 : 130} level="M" includeMargin={false} />
                                    </div>
                                    <div className="text-[10px] text-deep text-center mt-1 font-medium">
                                        Inquadra dal telefono per accedere
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2 mb-3">
                                <button onClick={() => setShowQr(expanded ? null : w.user_id)}
                                    data-testid={`expand-qr-${w.user_id}`}
                                    disabled={!w.active}
                                    className="flex-1 h-9 text-xs border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center gap-1 disabled:opacity-40">
                                    <QrCode className="w-3 h-3" /> {expanded ? "Riduci" : "Ingrandisci"}
                                </button>
                                <button onClick={() => rotateToken(w.user_id)}
                                    data-testid={`rotate-qr-${w.user_id}`}
                                    className="h-9 w-9 border border-[#1f2c44] hover:border-amber-400 hover:text-amber-400 rounded-md flex items-center justify-center text-elegant" title="Rigenera QR">
                                    <RefreshCw className="w-3 h-3" />
                                </button>
                            </div>

                            {w.last_login_at && (
                                <div className="text-[11px] text-elegant flex items-center gap-1 mb-3">
                                    <Clock className="w-3 h-3" /> Ultimo accesso: {new Date(w.last_login_at).toLocaleString("it-IT")}
                                </div>
                            )}

                            <div className="grid grid-cols-3 gap-2 my-3">
                                <div className="bg-deep border border-[#1f2c44] rounded p-2 text-center">
                                    <div className="font-display text-xl font-bold text-brand">{w.open_orders}</div>
                                    <div className="text-[9px] uppercase tracking-wider text-elegant">Ordini</div>
                                </div>
                                <div className="bg-deep border border-[#1f2c44] rounded p-2 text-center">
                                    <div className="font-display text-xl font-bold text-amber-400">{w.kitchen_pending_items}</div>
                                    <div className="text-[9px] uppercase tracking-wider text-elegant">In cucina</div>
                                </div>
                                <div className="bg-deep border border-[#1f2c44] rounded p-2 text-center">
                                    <div className="font-display text-xl font-bold text-green-400">{w.ready_pending_items}</div>
                                    <div className="text-[9px] uppercase tracking-wider text-elegant">Pronti</div>
                                </div>
                            </div>

                            {w.open_tables.length > 0 && (
                                <div className="mb-3">
                                    <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">Tavoli</div>
                                    <div className="flex flex-wrap gap-1">
                                        {w.open_tables.map((t) => (
                                            <button
                                                key={t.id}
                                                onClick={() => navigate(`/ordine/${t.id}`)}
                                                data-testid={`open-table-${t.number}`}
                                                className={`status-${t.status} text-xs px-2 py-1 rounded border-2 hover:scale-105 transition-transform`}
                                            >
                                                T{t.number}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-3 border-t border-[#1f2c44] mt-auto">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-elegant">Totale aperto</div>
                                    <div className="font-display font-bold text-brand">€ {w.total_amount.toFixed(2)}</div>
                                </div>
                                <button
                                    onClick={() => navigate("/mappa-sala")}
                                    data-testid={`view-${w.user_id}`}
                                    className="flex items-center gap-1 text-xs uppercase tracking-wider text-brand hover:gap-2 transition-all"
                                >
                                    <Eye className="w-3 h-3" /> Sala
                                    <ArrowRight className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {printAll && (
                <PrintAllQrModal
                    waiters={waiters.filter((w) => w.active && qrTokens[w.user_id])}
                    qrTokens={qrTokens}
                    buildQrUrl={buildQrUrl}
                    onClose={() => setPrintAll(false)}
                />
            )}
        </div>
    );
};

const PrintAllQrModal = ({ waiters, qrTokens, buildQrUrl, onClose }) => {
    const print = () => {
        const w = window.open("", "_blank", "width=900,height=1200");
        if (!w) { toast.error("Popup bloccato dal browser"); return; }
        const cards = waiters.map((wr) => {
            const url = buildQrUrl(qrTokens[wr.user_id]);
            // Genera SVG inline per ogni QR
            const id = `qr-${wr.user_id}`;
            return `
                <div class="card">
                    <div class="name">${wr.name}</div>
                    <div class="role">CAMERIERE · SMART TABLE</div>
                    <div class="qr" id="${id}" data-url="${url}"></div>
                    <div class="hint">Inquadra con la fotocamera del telefono per accedere</div>
                </div>
            `;
        }).join("");
        w.document.write(`
            <html>
                <head>
                    <title>QR Camerieri - Smart Table</title>
                    <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
                    <style>
                        @page { size: A4; margin: 12mm; }
                        body { font-family: system-ui, sans-serif; margin: 0; padding: 0; }
                        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8mm; }
                        .card { border: 1px solid #ddd; border-radius: 12px; padding: 16px; text-align: center; page-break-inside: avoid; }
                        .name { font-size: 22px; font-weight: bold; margin-bottom: 4px; }
                        .role { font-size: 10px; letter-spacing: 0.2em; color: #14E5BC; margin-bottom: 12px; }
                        .qr { display: flex; justify-content: center; margin-bottom: 8px; }
                        .qr canvas { display: block; }
                        .hint { font-size: 10px; color: #555; line-height: 1.4; }
                    </style>
                </head>
                <body>
                    <div class="grid">${cards}</div>
                    <script>
                        document.querySelectorAll('.qr').forEach(div => {
                            const url = div.getAttribute('data-url');
                            QRCode.toCanvas(div, url, { width: 240, margin: 1, errorCorrectionLevel: 'M' });
                        });
                        setTimeout(() => { window.print(); }, 500);
                    </script>
                </body>
            </html>
        `);
        w.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="print-all-qr-modal">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-lg w-full">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h2 className="font-display text-2xl font-bold">Stampa QR Camerieri</h2>
                        <div className="text-elegant text-sm mt-1">
                            {waiters.length} camerieri attivi · 2 QR per pagina A4
                        </div>
                    </div>
                    <button onClick={onClose}><X /></button>
                </div>
                <div className="bg-amber-900/20 border border-amber-700/30 rounded-md p-3 text-amber-200 text-xs mb-4">
                    Conserva i fogli stampati in luogo sicuro: chiunque scansioni il QR può accedere come quel cameriere.
                    Se un foglio viene smarrito, usa <span className="font-bold">↻ Rigenera</span> per invalidare il vecchio QR.
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                    <button onClick={print} disabled={waiters.length === 0}
                        data-testid="confirm-print-all-qr"
                        className="flex-1 h-12 bg-brand text-deep font-bold rounded-md flex items-center justify-center gap-2 disabled:opacity-40">
                        <PrinterIcon className="w-4 h-4" /> Stampa
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Terminali;
